import { useEffect, useState, useCallback } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { PublicConsent } from '@/api/publicEntities';
import MarkdownDocument from './MarkdownDocument';

/**
 * Чекбоксы согласия с юридически значимыми документами.
 * - Чекбоксы по умолчанию выключены (consent-spec.md §5.1).
 * - Каждый документ — кликабельная ссылка, открывает модалку с полным текстом.
 * - При изменении состояния вызывает onChange({ allAccepted, acceptedTypes, types, docs }).
 */
export default function ConsentCheckboxes({ types, onChange, className = '' }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState({}); // { type: bool }
  const [viewerDoc, setViewerDoc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    PublicConsent.getCurrent(types)
      .then(list => {
        if (cancelled) return;
        const arr = Array.isArray(list) ? list : [];
        const initial = Object.fromEntries(arr.map(d => [d.type, false]));
        setDocs(arr);
        setAccepted(initial);
        // Сразу сообщаем родителю про список типов — чтобы он мог
        // включить/выключить гейт «Продолжить» по факту наличия документов.
        onChange?.({
          allAccepted: arr.length === 0,
          acceptedTypes: [],
          types: arr.map(d => d.type),
          docs: arr
        });
      })
      .catch(e => {
        if (!cancelled) setError(e?.message || 'Failed to load consent documents');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // onChange намеренно вне зависимостей — иначе перевыполнялся бы fetch
    // при каждом ре-рендере родителя (стрелочные функции = новые ссылки).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(types || [])]);

  const emitChange = useCallback((nextAccepted, docList) => {
    const acceptedTypes = docList
      .filter(d => nextAccepted[d.type])
      .map(d => d.type);
    const allAccepted = docList.length > 0 && docList.every(d => nextAccepted[d.type]);
    onChange?.({
      allAccepted,
      acceptedTypes,
      types: docList.map(d => d.type),
      docs: docList
    });
  }, [onChange]);

  const handleToggle = (type) => {
    setAccepted(prev => {
      const next = { ...prev, [type]: !prev[type] };
      emitChange(next, docs);
      return next;
    });
  };

  // Пустой случай (документов нет / ошибка / загрузка) — не рендерим
  // визуальную обёртку, чтобы не было пустого места в формах.
  if (loading) {
    return (
      <div className={`flex items-center text-sm text-gray-500 ${className}`}>
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Загрузка документов...
      </div>
    );
  }

  if (error) {
    return <div className={`text-sm text-red-600 ${className}`}>Ошибка загрузки документов: {error}</div>;
  }

  if (!docs.length) {
    return null;
  }

  return (
    <Card className={className}>
      <CardContent className="pt-6 space-y-2">
        {docs.map(doc => (
          <label
            key={doc.id}
            className="flex items-start gap-2 text-sm leading-snug cursor-pointer"
          >
            <Checkbox
              checked={!!accepted[doc.type]}
              onCheckedChange={() => handleToggle(doc.type)}
              className="mt-0.5"
            />
            <span>
              Согласен с{' '}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); setViewerDoc(doc); }}
                className="text-blue-600 underline hover:text-blue-800"
              >
                {doc.name}
                {doc.version ? ` (v${doc.version})` : ''}
              </button>
            </span>
          </label>
        ))}

        <Dialog open={!!viewerDoc} onOpenChange={(open) => !open && setViewerDoc(null)}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {viewerDoc?.name}
                {viewerDoc?.version ? ` (v${viewerDoc.version})` : ''}
              </DialogTitle>
            </DialogHeader>
            <MarkdownDocument content={viewerDoc?.content} />
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
